import type { ErrorInfo, JSX, ReactNode } from "react";
import { Component } from "react";
import { Banner } from "./primitives.js";
import { useI18n } from "../i18n/react.js";

/**
 * Panel hata sınırı — Rapor 14 §10, Rapor 15 F1-37.
 *
 * ### ⭐ KURAL: **panel çökmesi görüşmeyi KESMEZ**
 * Sohbet, katılımcı listesi, transkript ve host paneli **ayrı ayrı** bu
 * sınırla sarılır. Sohbetteki bir `undefined.map` hatası video ızgarasını,
 * `MediaEngine`'i veya WebSocket'i düşürmemelidir.
 *
 * ### ⚠️ Video ızgarası bu sınırla SARILMAZ
 * Izgaranın yeniden monte edilmesi `<video>` elemanlarını yok eder ve
 * `attachRemote()` bağları kopar — kullanıcı herkesi kaybeder. Izgara
 * hatasının doğru cevabı sayfayı yeniden kurmak değil, karoyu atlamaktır.
 *
 * ### ⚠️ `key` ile sıfırlama
 * "Yeniden yükle" düğmesi yalnız `error`'ı temizlemez; `resetKey`'i artırarak
 * çocuk ağacı **taze monte eder**. Yalnız state temizlense, bozuk prop'la
 * aynı hata anında geri gelir ve düğme işe yaramaz görünür.
 */

interface Props {
  children: ReactNode;
  /** Telemetriye giden ad — panelin hangisi olduğu (`chat`, `participants`…). */
  name: string;
  onError?: (error: Error, panel: string) => void;
}

interface State {
  error: Error | null;
  resetKey: number;
}

export class PanelBoundary extends Component<Props, State> {
  override state: State = { error: null, resetKey: 0 };

  static getDerivedStateFromError(error: Error): Pick<State, "error"> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // ⚠️ `console.error` üretimde de kalır: panel çökmesi sessiz olmamalıdır.
    //    Kullanıcı görüşmeye devam eder, ama olay kaydedilir.
    console.error(`[panel:${this.props.name}]`, error, info.componentStack);
    this.props.onError?.(error, this.props.name);
  }

  private readonly reset = (): void => {
    this.setState((s) => ({ error: null, resetKey: s.resetKey + 1 }));
  };

  override render(): ReactNode {
    if (this.state.error !== null) {
      return <PanelCrashNotice error={this.state.error} onReset={this.reset} />;
    }
    return <div key={this.state.resetKey}>{this.props.children}</div>;
  }
}

/**
 * ⭐ **BU HATA PANELİN DEĞİL, DAĞITIMIN HATASI MI?**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 **"DURDUK YERE TÜM KULLANICILAR 'BU PANEL YÜKLENEMEDİ' ALDI."**
 * ═══════════════════════════════════════════════════════════════════════════
 * Bir panel çökmesi normalde **tek kullanıcıda** ve o kullanıcının verisiyle
 * olur. Aynı anda herkeste olması başka bir şeye işaret eder: uygulama
 * yeniden dağıtıldı. Cloudflare Pages her yayında dosya adlarına yeni bir
 * özet ekler (`transcript-B9IpHzVu.js` → `transcript-RrJzEpM4.js`). Sekmesi
 * açık kalan kullanıcının elindeki ESKİ `index` hâlâ eski adı istiyor; o
 * dosya artık sunucuda yok, dinamik `import()` reddediliyor ve React bunu
 * bir render hatası olarak sınıra taşıyor.
 *
 * ### ⚠️ "Yeniden yükle" düğmesi bu durumda İŞE YARAMAZ
 * Alt ağacı yeniden monte etmek, var olmayan aynı dosyayı yeniden istemektir:
 * hata anında geri gelir ve kullanıcı düğmeye basıp basıp bir şey olmadığını
 * görür. Tek çare **sayfayı yenilemek**tir — o zaman yeni `index` ve yeni
 * dosya adları gelir.
 *
 * ⚠️ Ayrım mesaj metninden yapılır ve tarayıcılar farklı yazar; üç ailenin de
 *    kalıbı tutuluyor. Şüphede kalınırsa normal panel hatası varsayılır:
 *    gereksiz yere sayfa yenilemek, kullanıcının yazdığı sohbet mesajını
 *    silmek olurdu.
 */
export function isStaleChunkError(error: Error): boolean {
  const s = `${error.name} ${error.message}`;
  return (
    /ChunkLoadError/i.test(s) ||
    // Chrome / Vite
    /Failed to fetch dynamically imported module/i.test(s) ||
    // Firefox
    /error loading dynamically imported module/i.test(s) ||
    // Safari
    /Importing a module script failed/i.test(s)
  );
}

/**
 * Yedek görünüm — çevirmene ihtiyaç duyduğu için **fonksiyon** bileşendir
 * (sınıf bileşenleri hook kullanamaz).
 */
function PanelCrashNotice(props: { error: Error; onReset: () => void }): JSX.Element {
  const i18n = useI18n();
  const stale = isStaleChunkError(props.error);

  return (
    <Banner
      level="warn"
      action={{
        label: stale ? i18n.t("error.refreshPage") : i18n.t("error.reload"),
        // ⚠️ Eski dosya için alt ağacı yeniden monte etmek anlamsızdır: aynı
        //    var olmayan dosya yeniden istenir ve hata anında geri gelir.
        onAct: stale ? () => location.reload() : props.onReset,
      }}
    >
      {stale ? i18n.t("error.appUpdated") : i18n.t("error.panelCrashed")}
    </Banner>
  );
}
